import { getNotifications, markAllNotificationsRead, markNotificationRead } from '@/lib/api/notificationApi'
import { GetNotificationsQuery } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { fetchCurrentUserThunk } from './currentUserSlice'
import { QueryState } from './queryStateType'

const PAGE_SIZE = 20

export type NotificationType = NonNullable<
  NonNullable<NonNullable<GetNotificationsQuery['currentUser']>['notifications']['nodes']>[number]
>

type NotificationState = QueryState & {
  unreadCount: number
  hasNextPage: boolean
  currentPage: number
}

const initialState: NotificationState = {
  isLoading: false,
  loaded: false,
  error: null,
  unreadCount: 0,
  hasNextPage: false,
  currentPage: 0,
}

const notificationAdapter = createEntityAdapter<NotificationType>()

export const notificationSlice = createSlice({
  name: 'notifications',
  initialState: notificationAdapter.getInitialState<NotificationState>(initialState),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.unreadCount = action.payload.notifications.totalUnreadCount
      })
      .addCase(markAllNotificationsReadThunk.fulfilled, state => {
        state.unreadCount = 0
        notificationAdapter.updateMany(
          state,
          Object.values(state.entities)
            .filter(notification => !notification.read)
            .map(notification => ({
              id: notification.id,
              changes: { read: true },
            }))
        )
      })
      .addCase(getNotificationsThunk.pending, (state, action) => {
        state.isLoading = true
      })
      .addCase(getNotificationsThunk.fulfilled, (state, action) => {
        const isFirstPage = action.meta.arg.page === 1
        if (isFirstPage) {
          notificationAdapter.setAll(state, action.payload.nodes)
        } else {
          notificationAdapter.addMany(state, action.payload.nodes)
        }
        state.hasNextPage = action.payload.hasNextPage
        state.currentPage = action.meta.arg.page
        state.unreadCount = action.payload.totalUnreadCount
        state.loaded = true
        state.isLoading = false
      })
      .addCase(getNotificationsThunk.rejected, (state, action) => {
        state.error = action.error.message
        state.isLoading = false
      })
      .addCase(markNotificationReadThunk.fulfilled, (state, action) => {
        state.unreadCount -= state.unreadCount > 0 ? 1 : 0
        notificationAdapter.updateOne(state, {
          id: action.meta.arg.notificationId,
          changes: { read: true },
        })
      })
  },
})

export const markAllNotificationsReadThunk = createAsyncThunk(
  'notifications/markAllNotificationsRead',
  async (_payload, thunkAPI) => {
    const { errors } = await markAllNotificationsRead()
    return !errors || thunkAPI.rejectWithValue('failed to mark all notifications read')
  }
)

export const getNotificationsThunk = createAsyncThunk(
  'notifications/getNotifications',
  async ({ page }: { page: number }, thunkAPI) => {
    const { data } = await getNotifications(PAGE_SIZE, page)
    const notifications = data?.currentUser?.notifications
    if (!notifications) {
      return thunkAPI.rejectWithValue('failed to load notifications')
    }
    return {
      nodes: (notifications.nodes as NotificationType[]) || [],
      hasNextPage: notifications.pageInfo?.hasNextPage || false,
      totalUnreadCount: notifications.totalUnreadCount ?? 0,
    }
  }
)

export const markNotificationReadThunk = createAsyncThunk(
  'notifications/markNotificationRead',
  async ({ notificationId }: { notificationId: string }, thunkAPI) => {
    const { errors } = await markNotificationRead(notificationId)
    return !errors || thunkAPI.rejectWithValue('failed to mark notification read')
  }
)

export const selectUnreadNotificationsCount = (state: RootState) => state.notifications.unreadCount

export const notificationsSelectors = notificationAdapter.getSelectors<RootState>(state => state.notifications)

export default notificationSlice.reducer
